import { useCallback, useMemo } from "react";

import {
  useOnlineDrivers,
  useSessionUser,
  useWorkspaceConnections,
} from "@web-app/api";
import { DutyStatus, Order, Route, User } from "@web-app/types";

interface WorkspaceEntry {
  assignees: Map<string, WorkspaceAssignee>;
  avatarUrl: string | null;
  id: string;
  name: string;
}

export type WorkspaceAssignee = {
  avatarUrl: string | null;
  driverProfileId?: string;
  dutyStatus?: DutyStatus;
  id: string;
  name: string | null;
  orders: Order[];
  routes: Route[];
};

export default function useConnectionsDriversView({
  assigneesMap,
  unroutedOrders,
  routes,
  searchTerm,
}: {
  assigneesMap?: Map<string, User>;
  routes: Route[];
  searchTerm: string;
  unroutedOrders: Order[];
}) {
  const { data: connections, isFetching: isWorkspaceConnectionsLoading } =
    useWorkspaceConnections();
  const { data: currentUser } = useSessionUser();
  const { data: onlineDrivers, isLoading: isOnlineDriversLoading } =
    useOnlineDrivers();

  const updateWorkspaceAssignees = useCallback(
    (task: Order | Route, workspaceEntry: WorkspaceEntry) => {
      // Use task.assigneeId for grouping - works for both orders and routes
      const assigneeId = task.assigneeId || "no-assignee";
      const assignee = task.assigneeId
        ? assigneesMap?.get(task.assigneeId)
        : null;

      if (!workspaceEntry.assignees.has(assigneeId)) {
        workspaceEntry.assignees.set(assigneeId, {
          avatarUrl: assignee?.image ?? null,
          driverProfileId: assignee?.id,
          dutyStatus: assignee?.driverDetails?.dutyStatus,
          id: assigneeId,
          name:
            assignee?.name ??
            (assigneeId === "no-assignee" ? "No Assignee" : "Loading..."),
          orders: [],
          routes: [],
        });
      }

      if (!("ordersOrder" in task)) {
        workspaceEntry.assignees.get(assigneeId)!.orders.push(task as Order);
      } else {
        workspaceEntry.assignees.get(assigneeId)!.routes.push(task as Route);
      }
    },
    [assigneesMap],
  );

  const ordersGroupedByWorkspace = useMemo(() => {
    const allTasks = [...unroutedOrders, ...routes];

    const workspacesWithUnsortedAssignees = allTasks.reduce(
      (workspaces, task) => {
        let workspace = null;

        // Check if task belongs to current workspace
        if (task.workspaceId === currentUser?.currentWorkspaceId) {
          workspace = currentUser?.currentWorkspace;
        } else if (connections?.length) {
          // Find connection for connected workspace tasks
          const connection = connections?.find(
            (conn) =>
              conn.workspaceId == task.workspaceId ||
              conn.connectedWorkspaceId == task.workspaceId,
          );
          if (connection) {
            workspace =
              connection.workspaceId == task.workspaceId
                ? connection.workspace
                : connection.connectedWorkspace;
          }
        }

        if (!workspace) return workspaces;

        // Find or create workspace entry
        let workspaceEntry = workspaces.find((w) => w.id === workspace.id);
        if (!workspaceEntry) {
          workspaceEntry = {
            assignees: new Map(),
            avatarUrl: workspace.avatarUrl,
            id: workspace.id,
            name: workspace.name,
          };
          workspaces.push(workspaceEntry);
        }

        // Get or create assignee map entry
        updateWorkspaceAssignees(task, workspaceEntry);

        return workspaces;
      },
      [] as WorkspaceEntry[],
    );

    // Sort assignees within each workspace
    return workspacesWithUnsortedAssignees.map((workspace) => {
      const sortedAssignees = new Map(
        Array.from(workspace.assignees.entries()).sort((a, b) => {
          if (a[0] === "no-assignee") return -1;
          if (b[0] === "no-assignee") return 1;
          return (a[1].name || "").localeCompare(b[1].name || "");
        }),
      );

      return {
        ...workspace,
        assignees: sortedAssignees,
      };
    });
  }, [
    unroutedOrders,
    routes,
    connections,
    currentUser,
    updateWorkspaceAssignees,
  ]);

  const unassignedOnlineDrivers = useMemo(() => {
    const assignedDriverIds = new Set(
      [...unroutedOrders, ...routes]
        .map((task) => task.assigneeId)
        .filter((assigneeId): assigneeId is string => !!assigneeId),
    );
    const normalizedSearchTerm = searchTerm.trim().toLocaleLowerCase();

    return (onlineDrivers ?? [])
      .filter(
        (driver) =>
          !assignedDriverIds.has(driver.id) &&
          (!normalizedSearchTerm ||
            driver.name.toLocaleLowerCase().includes(normalizedSearchTerm)),
      )
      .map<WorkspaceAssignee>((driver) => ({
        avatarUrl: driver.image ?? null,
        driverProfileId: driver.id,
        dutyStatus: driver.driverProfile?.dutyStatus,
        id: driver.id,
        name: driver.name,
        orders: [],
        routes: [],
      }))
      .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""));
  }, [onlineDrivers, routes, searchTerm, unroutedOrders]);

  return {
    connections: connections ?? [],
    isOnlineDriversLoading,
    isWorkspaceConnectionsLoading,
    ordersGroupedByWorkspace,
    unassignedOnlineDrivers,
  };
}
